import { IconRepositories } from '../../../common/icons';
import Picker from '../../../common/components/picker/picker';
import { searchProjects } from '../../projects/contexts/projects';
import {
    BRANCH_MAX,
    NAME_MAX,
    REMOTE_MAX,
    REPOSITORY_KIND_OPTIONS,
    STATUS_OPTIONS,
    protocolOf,
} from '../utils/form';

import '../../clients/styles/form.css';

// `projectLocked` is true while editing. The backend refuses to move a repo to
// another project, and says why: it would silently invalidate every team scope
// pointing at it. The field still shows — the screen has to say where this
// repo lives — but it cannot change.

export default function RepositoryFields({
    values, errors = {}, onChange, disabled, projectLocked, visible, projectLabel,
}) {
    const set = (field) => (e) => onChange(field, e.target.value);
    // `visible` undefined means show everything, which is what the edit form
    // does: editing is one page, never a walk through steps.
    const show = (field) => !visible || visible.includes(field);
    const protocol = protocolOf(values.remote);

    return (
        <div className="client-form__grid">
            {show('project_id') && (
                <label className="client-form__field">
                    <span className="client-form__label">
                        Proyecto <span className="client-form__required">*</span>
                    </span>
                    <span className="client-form__control">
                        <Picker
                            value={values.project_id}
                            onChange={(id) => onChange('project_id', id)}
                            fetchOptions={searchProjects}
                            cacheKey="projects"
                            labelOf={project => project.name}
                            subtitleOf={project => project.slug}
                            selectedLabel={projectLabel}
                            placeholder="Elegir proyecto..."
                            disabled={disabled || projectLocked}
                            invalid={!!errors.project_id}
                        />
                    </span>
                    {errors.project_id
                        ? <span className="client-form__error">{errors.project_id}</span>
                        : projectLocked && (
                            <span className="client-form__note">
                                Un repositorio no cambia de proyecto: moverlo invalidaría en
                                silencio los alcances de equipo que lo apuntan.
                            </span>
                        )}
                </label>
            )}

            {show('kind') && (
                <label className="client-form__field">
                    <span className="client-form__label">
                        Tipo <span className="client-form__required">*</span>
                    </span>
                    <span className="client-form__control">
                        <select
                            className="client-form__input client-form__input--select"
                            value={values.kind}
                            onChange={set('kind')}
                            disabled={disabled}
                        >
                            {REPOSITORY_KIND_OPTIONS.map(option => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                        </select>
                    </span>
                    {errors.kind && <span className="client-form__error">{errors.kind}</span>}
                </label>
            )}

            {show('name') && (
                <label className="client-form__field">
                    <span className="client-form__label">
                        Nombre <span className="client-form__required">*</span>
                    </span>
                    <span className="client-form__control">
                        <input
                            className={`client-form__input ${errors.name ? 'client-form__input--invalid' : ''}`}
                            type="text"
                            value={values.name}
                            onChange={set('name')}
                            maxLength={NAME_MAX}
                            placeholder="mastermind_core"
                            disabled={disabled}
                            required
                        />
                        <span className="client-form__icon" aria-hidden="true"><IconRepositories /></span>
                    </span>
                    {errors.name && <span className="client-form__error">{errors.name}</span>}
                </label>
            )}

            {show('main_branch') && (
                <label className="client-form__field">
                    <span className="client-form__label">Rama principal</span>
                    <span className="client-form__control">
                        <input
                            className={`client-form__input client-form__input--mono ${errors.main_branch ? 'client-form__input--invalid' : ''}`}
                            type="text"
                            value={values.main_branch}
                            onChange={set('main_branch')}
                            maxLength={BRANCH_MAX}
                            placeholder="main"
                            disabled={disabled}
                        />
                    </span>
                    {errors.main_branch && <span className="client-form__error">{errors.main_branch}</span>}
                </label>
            )}

            {show('remote') && (
                <label className="client-form__field client-form__field--wide">
                    <span className="client-form__label">
                        Remoto <span className="client-form__required">*</span>
                    </span>
                    <span className="client-form__control">
                        <input
                            className={`client-form__input client-form__input--mono ${errors.remote ? 'client-form__input--invalid' : ''}`}
                            type="text"
                            value={values.remote}
                            onChange={set('remote')}
                            maxLength={REMOTE_MAX}
                            placeholder="https://github.com/org/repo.git · git@github.com:org/repo.git"
                            disabled={disabled}
                            required
                        />
                    </span>
                    {errors.remote
                        ? <span className="client-form__error">{errors.remote}</span>
                        : (
                            <span className="client-form__note">
                                {/* The protocol is DERIVED from the remote, never chosen: it
                                    is what tells the machine whether it will be asked for a
                                    token or for a key. Showing it as it is typed is the only
                                    way that derivation is visible before saving. */}
                                {protocol === 'https' && 'Clona por HTTPS: la máquina va a pedir un token.'}
                                {protocol === 'ssh' && 'Clona por SSH: la máquina va a pedir una llave.'}
                                {!protocol && 'Sin él nadie puede clonar. El protocolo sale de acá, no se elige.'}
                            </span>
                        )}
                </label>
            )}

            {show('status') && (
                <label className="client-form__field">
                    <span className="client-form__label">Estado del registro</span>
                    <span className="client-form__control">
                        <select
                            className="client-form__input client-form__input--select"
                            value={values.status}
                            onChange={set('status')}
                            disabled={disabled}
                        >
                            {STATUS_OPTIONS.map(option => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                        </select>
                    </span>
                    {errors.status && <span className="client-form__error">{errors.status}</span>}
                </label>
            )}
        </div>
    );
}
